import { useEffect, useRef, useState } from 'react';
import {
  Button, Empty, Form, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { DatabaseOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import {
  listModuleRecords, createModuleRecordGeneric, updateModuleRecordGeneric, deleteModuleRecordGeneric,
  getLocationMasterOptions,
} from '../../../services/adminApi';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;
const idOf = (record) => record?._id ?? record?.id;

// ── tax value / history helpers ──────────────────────────────────────────────
function taxHistoryList(record) {
  return Array.isArray(record?.taxHistory) ? record.taxHistory : [];
}

function currentTaxValue(record) {
  const root = record?.taxValue;
  if (root !== undefined && root !== null && root !== '') return Number(root);
  const last = taxHistoryList(record).at(-1);
  const value = last?.taxValue ?? last?.value;
  return value !== undefined && value !== null && value !== '' ? Number(value) : undefined;
}

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

function latestTaxUpdatedAt(record) {
  const last = taxHistoryList(record).at(-1);
  return last?.updatedAt ?? last?.timestamp ?? record?.updatedAt;
}

// ── Location + Tax editor ────────────────────────────────────────────────────
// Location is chosen from the locationMasters collection. Once a record exists,
// the location is locked and only the tax is editable (every tax change is kept
// in taxHistory with its timestamp).
function LocationEditor({ open, record, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);
  const isEdit = Boolean(record);

  useEffect(() => {
    if (!open) return;
    queueMicrotask(() => {
      form.resetFields();
      form.setFieldsValue({ name: record?.name ?? undefined, taxValue: currentTaxValue(record) });
      // Seed the select with the current location so it renders on edit without a search.
      setOptions(record?.name ? [{ label: record.name, value: record.name }] : []);
    });
  }, [open, record, form]);

  function runSearch(search) {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!search || search.trim().length < 2) { setOptions([]); return; }
    setSearching(true);
    searchTimer.current = setTimeout(() => {
      getLocationMasterOptions(search.trim())
        .then(setOptions)
        .catch(() => setOptions([]))
        .finally(() => setSearching(false));
    }, 350);
  }

  async function save() {
    const values = await form.validateFields();
    const taxValue = Number(values.taxValue);
    const payload = {
      name: values.name,
      // Root taxValue stays the current value — configured submission lookups
      // read locations.taxValue directly. The gateway appends {taxValue,
      // updatedAt, updatedBy} to taxHistory (config-driven appendHistory).
      taxValue,
      taxHistory: { taxValue },
    };
    setSaving(true);
    try {
      if (isEdit) await updateModuleRecordGeneric('locations', idOf(record), payload);
      else await createModuleRecordGeneric('locations', payload);
      message.success(isEdit ? 'Tax updated' : 'Location and tax created');
      onSaved();
    } catch (error) {
      message.error(error?.message || 'Could not save location and tax');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={save}
      confirmLoading={saving}
      destroyOnHidden
      title={isEdit ? `Edit Tax — ${record?.name ?? ''}` : 'New Location'}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Location"
          rules={[{ required: true, message: 'Location is required' }]}
          tooltip={isEdit ? 'Location cannot be changed once created — only the tax is editable.' : 'Search and pick from the location master.'}
        >
          <Select
            showSearch
            disabled={isEdit}
            placeholder="Type at least 2 characters to search…"
            filterOption={false}
            onSearch={runSearch}
            notFoundContent={searching ? 'Searching…' : null}
            options={options}
          />
        </Form.Item>

        <Form.Item
          name="taxValue"
          label="Tax"
          rules={[
            { required: true, message: 'Tax is required' },
            { type: 'number', min: 0, max: 100, message: 'Tax must be between 0 and 100' },
          ]}
        >
          <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.01} suffix="%" placeholder="8.875" autoFocus={isEdit} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Tax timeline (expanded row) ──────────────────────────────────────────────
function TaxTimeline({ record }) {
  const history = [...taxHistoryList(record)].reverse(); // newest first
  if (!history.length) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tax history yet" />;
  return (
    <Space direction="vertical" size={4} style={{ padding: '4px 8px' }}>
      {history.map((entry, index) => (
        <Space key={index} size={10}>
          <Tag color={index === 0 ? 'green' : 'default'} style={{ margin: 0 }}>
            {entry.taxValue ?? entry.value}%
          </Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>{formatDateTime(entry.updatedAt ?? entry.timestamp)}</Text>
          {index === 0 && <Text type="secondary" style={{ fontSize: 12 }}>(current)</Text>}
        </Space>
      ))}
    </Space>
  );
}

function LocationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  function load() {
    setLoading(true);
    listModuleRecords('locations')
      .then(({ items: data }) => setItems(data))
      .catch((error) => message.error(error?.message || 'Could not load locations'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { queueMicrotask(load); }, []);

  async function remove(id) {
    try {
      await deleteModuleRecordGeneric('locations', id);
      message.success('Location deleted');
      load();
    } catch (error) {
      message.error(error?.message || 'Could not delete location');
    }
  }

  const columns = [
    { title: 'Location', dataIndex: 'name', key: 'name' },
    {
      title: 'Tax',
      key: 'taxValue',
      width: 100,
      render: (_, record) => {
        const value = currentTaxValue(record);
        return Number.isFinite(value) ? <Tag color="blue">{value}%</Tag> : '—';
      },
    },
    { title: 'Tax updated', key: 'taxUpdatedAt', render: (_, record) => formatDateTime(latestTaxUpdatedAt(record)) },
    { title: 'Created', key: 'createdAt', responsive: ['lg'], render: (_, record) => formatDateTime(record?.createdAt) },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit tax">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setEditing(record); setEditorOpen(true); }} />
          </Tooltip>
          <Popconfirm title="Delete this location?" onConfirm={() => remove(idOf(record))}>
            <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="fg-section admin-config-section">
      <AppCard
        title={`${items.length} location${items.length === 1 ? '' : 's'}`}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load}>Reload</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); setEditorOpen(true); }}>
              New location
            </Button>
          </Space>
        )}
      >
        <Table
          rowKey={idOf}
          columns={columns}
          dataSource={items}
          loading={loading}
          size="small"
          pagination={false}
          className="fg-table"
          expandable={{
            expandedRowRender: (record) => <TaxTimeline record={record} />,
            rowExpandable: (record) => taxHistoryList(record).length > 0,
          }}
          locale={{ emptyText: 'No locations yet. Click "New location" to add one.' }}
        />
      </AppCard>
      <LocationEditor
        open={editorOpen}
        record={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={() => { setEditorOpen(false); load(); }}
      />
    </div>
  );
}

export default function MastersSection() {
  return (
    <div className="fg-section">
      <AppCard icon={<DatabaseOutlined />} title="Location Tax Master">
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          Pick a location from the master list and set its tax. Once added, only the tax can be changed — every
          change is kept in a timeline with its timestamp. Expand a row to see the full tax history.
        </Text>
      </AppCard>
      <LocationsPanel />
    </div>
  );
}
